import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { mdiAccountOutline, mdiPlus, mdiRobot } from "@mdi/js";
import Main from "../../components/layout/Main";
import MdiIcon from "../../components/device/app/MdiIcon";
import { Chips } from "../Home/Floors";
import { FILTERS } from "../../automation/format";
import { CARD_COLORS } from "../../automation/look";
import { useAutomationFeature } from "../../automation/AutomationProvider";
import {
  useAutomations,
  useCanManageAutomations,
  useConfirm,
  useRunAutomation,
  useToggleAutomation,
} from "../../automation/useAutomations";
import AutomationCard from "./AutomationCard";
import { AUTOMATION_PATHS } from "./paths";
import "../../assets/styles/app-automation.css";

// The Automation tab (app/screens/Automation/AutomationHome.js): every
// automation in the home the person may see, with filter chips, and New for
// owners.
function AutomationHome() {
  const navigate = useHistory();
  const { lookup, on, checked } = useAutomationFeature();
  const canManage = useCanManageAutomations();
  const { items, loading, error, reload, replace } = useAutomations();
  const { confirm, dialog } = useConfirm();
  const run = useRunAutomation(confirm, reload);
  const toggle = useToggleAutomation(replace);
  const [filter, setFilter] = useState("all");

  // what the device does by itself shows on that device's page only
  const shown = useMemo(() => (items ?? []).filter((item) => !item.readOnly), [items]);
  const chips = useMemo(
    () => [{ key: "all", label: "All" }, ...FILTERS.filter((chip) => shown.some((item) => chip.types.includes(item.type)))],
    [shown]
  );
  const selected = chips.some((chip) => chip.key === filter) ? filter : "all";
  const types = FILTERS.find((chip) => chip.key === selected)?.types;
  const list = types ? shown.filter((item) => types.includes(item.type)) : shown;
  const open = (item, color) => navigate.push(AUTOMATION_PATHS.DETAIL, { id: item.id, automation: item, color });

  let body;
  if (items === null || (!checked && !on)) {
    body = (
      <div className="hx-auto-list">
        <div className="hx-skel hx-auto-skel" />
        <div className="hx-skel hx-auto-skel" />
        <div className="hx-skel hx-auto-skel" />
      </div>
    );
  } else if (!list.length) {
    body = (
      <div className="hx-empty-page hx-auto-empty" role={error ? "alert" : undefined}>
        <MdiIcon path={mdiRobot} size={64} />
        <p>{error ? "Couldn't load automations" : on ? "No automations yet" : "Automations aren't on for this home"}</p>
        <span>
          {error ||
            (canManage
              ? "Use New to switch devices at set times, at sunrise or sunset, or when a sensor sees something."
              : "Automations that use your devices show up here.")}
        </span>
        {error ? (
          <button type="button" className="hx-btn" onClick={reload}>
            Try again
          </button>
        ) : null}
      </div>
    );
  } else {
    body = (
      <div className="hx-auto-list" aria-busy={loading || undefined}>
        {/* cards take the Quick Actions colours in turn, as in the app */}
        {list.map((item, index) => {
          const color = CARD_COLORS[index % CARD_COLORS.length];
          return (
            <AutomationCard
              key={item.id}
              automation={item}
              lookup={lookup}
              color={color}
              onOpen={() => open(item, color)}
              onToggle={(enabled) => toggle(item, enabled)}
              onRun={() => run(item)}
            />
          );
        })}
      </div>
    );
  }

  return (
    <Main header="tab" title="Automation">
      <div className="hx-auto-head">
        <h2 className="hx-auto-title">Automations</h2>
        {canManage && on ? (
          <button type="button" className="hx-auto-new" onClick={() => navigate.push(AUTOMATION_PATHS.CREATE)}>
            <MdiIcon path={mdiPlus} size={18} />
            New
          </button>
        ) : null}
      </div>
      {shown.length ? <Chips label="Automation types" items={chips} active={selected} onSelect={setFilter} /> : null}
      {body}
      {items !== null && !canManage && shown.length ? (
        <p className="hx-auto-hint">
          <MdiIcon path={mdiAccountOutline} size={18} />
          You're a guest in this home. You can run tap-to-run automations made of your devices; only owners can add or change
          automations.
        </p>
      ) : null}
      {dialog}
    </Main>
  );
}

export default AutomationHome;
